import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { doc, onSnapshot } from "firebase/firestore";

import { patchManualProduct } from "../actions/products";
import { setGlobalAlert } from "../actions/common";
import { projectFirestore } from "../firebase";
import {
  observeChannelFlip,
  seedChannelStatus,
} from "../helpers/channelPublish";

function pendingProductIds(products, job) {
  const ids = new Set();
  (products || []).forEach((product) => {
    const channels = product?.channels;
    if (!product?.id || !channels || typeof channels !== "object") return;
    const pending = Object.values(channels).some(
      (entry) => entry?.status === "pending" || entry === "pending"
    );
    if (pending) ids.add(product.id);
  });
  const jobProductId = job?.productId || job?.result?.id;
  const jobChannels = job?.result?.channels;
  if (
    jobProductId &&
    jobChannels &&
    Object.values(jobChannels).some((entry) => entry?.status === "pending")
  ) {
    ids.add(jobProductId);
  }
  return Array.from(ids).sort();
}

/**
 * Follows `manualProducts/{id}` while a channel publish is pending.
 * The chip and the completion toast both come from that document.
 */
const ChannelPublishWatcher = () => {
  const dispatch = useDispatch();
  const manualProducts = useSelector(
    (state) => state?.products?.manualProducts || []
  );
  const job = useSelector((state) => state?.products?.productCreateJob);
  const ids = useMemo(
    () => pendingProductIds(manualProducts, job),
    [manualProducts, job]
  );
  const idKey = ids.join(",");

  useEffect(() => {
    if (!ids.length) return undefined;
    const jobProductId = job?.productId || job?.result?.id;
    if (jobProductId) seedChannelStatus(jobProductId, job?.result?.channels);
    const unsubs = ids.map((id) =>
      onSnapshot(
        doc(projectFirestore, "manualProducts", id),
        (snap) => {
          if (!snap.exists()) return;
          const data = snap.data() || {};
          const channels = data.channels || {};
          dispatch(
            patchManualProduct({
              id,
              channels,
              ...(Array.isArray(data.activeShops)
                ? { activeShops: data.activeShops }
                : {}),
            })
          );
          observeChannelFlip(id, channels, ({ name, listingUrl }) => {
            dispatch(
              setGlobalAlert({
                type: "success",
                message: `Live on ${name}.`,
                href: listingUrl || null,
                linkLabel: "View listing",
              })
            );
          });
        },
        (err) => {
          console.warn(
            "[channel-publish] product snapshot failed",
            id,
            err?.code || err?.message || err
          );
        }
      )
    );
    return () => unsubs.forEach((unsub) => unsub());
    // idKey is the subscription set. `ids` is a new array whenever products change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, idKey]);

  return null;
};

export default ChannelPublishWatcher;
